import * as React from 'react'
import { useRouter } from 'next/router'

import {
  i18n,
  Routes,
  useAddToBasket,
  useSiteDefinition,
  useTheme,
} from '@thg-commerce/enterprise-core'
import { useBasket } from '@thg-commerce/enterprise-core/src/Basket/hooks/useBasketId'
import { BundleSku } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/ResponsiveBuildYourOwnBundleResolver'
import { formatPrice } from '@thg-commerce/enterprise-pricing'
import {
  BundleProduct,
  ResponsiveBuildYourOwnBundleProducts,
} from '@thg-commerce/enterprise-widget-responsive-build-your-own-bundle-products'

import { ProgressIndicator, ProgressIndicatorType } from './ProgressIndicator'
import {
  AddToBasketErrorMessage,
  AddTotalToBasket,
  BuildYourOwnBundleContainer,
  StyledButton,
  StyledLeftMenu,
  StyledLi,
  StyledMenuTitle,
  StyledTotalTitle,
  Subtitle,
  SummaryContainer,
  Title,
  TitleContainer,
  TotalContent,
  TotalContentPrice,
  TotalContentText,
  TotalReceiptBox,
} from './styles'

export interface BundleCategory {
  categoryTitle: string
  enableQuantitySelector?: boolean
  id: string
  maxProducts: number
  productBlocks: BundleSku[]
}

export type BuildYourOwnBundleProps = {
  className?: string
  showBundlePageTitle?: boolean
  bundleSubTitle?: string
  summaryAmount?: string
  expandAllCategories?: boolean
  maxProducts: number
  banners: BundleCategory[]
  summaryDisplayType?: string
  unlimitedProducts?: boolean
}

enum SummaryDisplayType {
  DIAL = 'dial',
  RECEIPT = 'receipt',
}

export const MAX_QUANTITY_SELECTION = 200

export const BuildYourOwnBundle = (props: BuildYourOwnBundleProps) => {
  const [disableAddToBasket, setDisableAddToBasket] = React.useState<boolean>(
    false,
  )
  const theme = useTheme()
  const [showAddToBasketErrorMessage, setShowErrorMessage] = React.useState<
    boolean
  >(false)

  const focusAddToBasketErrorMessage = React.useRef<HTMLDivElement>(null)

  const [selectedBundleProducts, setSelectedBundleProducts] = React.useState<
    BundleProduct[]
  >([])

  const [focusedAccordionItem, setFocusedAccordionItem] = React.useState<
    number | null
  >(null)

  const router = useRouter()

  const errorOccurredRef = React.useRef<boolean>(false)

  const { execute: addToBasket } = useAddToBasket({
    errorHandler: () => {
      errorOccurredRef.current = true
      setShowErrorMessage(true)
    },
    forceAddToBasket: true,
  })

  const { loading: basketLoading } = useBasket()

  const { defaultLocale, defaultCurrency } = useSiteDefinition()

  React.useEffect(() => {
    if (showAddToBasketErrorMessage && focusAddToBasketErrorMessage.current) {
      focusAddToBasketErrorMessage.current.focus()
      focusAddToBasketErrorMessage.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      })
    }
  }, [showAddToBasketErrorMessage])

  const { totalPrice, totalProductsInBundle } = selectedBundleProducts.reduce(
    (
      total: { totalPrice: number; totalProductsInBundle: number },
      product: BundleProduct,
    ): {
      totalPrice: number
      totalProductsInBundle: number
    } => {
      return {
        totalPrice: total.totalPrice + product.price * product.quantity,
        totalProductsInBundle: total.totalProductsInBundle + product.quantity,
      }
    },
    { totalPrice: 0, totalProductsInBundle: 0 },
  )

  const formattedTotalPrice = formatPrice(
    defaultCurrency,
    totalPrice.toFixed(2),
    defaultLocale,
  )

  const i18nText = {
    categoriesText: i18n('widget.buildyourbundle.categories.text'),
    simpleItemsText: i18n('widget.buildyourbundle.items.text'),
    bundleTitleText: i18n('widget.buildyourbundle.title.text'),
    yourTotalText: i18n('widget.buildyourbundle.yourtotal.text'),
    addToBasket: i18n('basket.addproduct.text'),
    topProgressIndicatorBar: {
      totalText: i18n('widget.buildyourbundle.yourbundletotal.text'),
      itemsText: i18n(
        'widget.buildyourbundle.ofitems.text',
        `${props.maxProducts}`,
      ),
      itemsAlternateText: i18n('widget.buildyourbundle.items.text'),
      addToBagText: i18n('basket.addproduct.text'),
    },
    bottomProgressIndicatorBar: {
      itemsText: i18n('widget.buildyourbundle.items.text'),
      itemsAlternateText: i18n(
        'widget.buildyourbundle.ofitems.text',
        `${props.maxProducts}`,
      ),
      addToBagText: i18n('widget.buildyourbundle.addtobasketsimple.text'),
    },
    addToBasketError: i18n('basket.addtobasketmultiple.error.text'),
  }

  const bundleCategories = props.banners.map((item, index) => {
    return (
      <StyledLi
        key={`category-button-${index}`}
        lastItem={index === props.banners.length - 1}
      >
        <StyledButton
          data-testid={`category-button-${index}`}
          onClick={() => setFocusedAccordionItem(index)}
        >
          {item.categoryTitle}
        </StyledButton>
      </StyledLi>
    )
  })

  const handleAddToBasketCallback = async () => {
    if (selectedBundleProducts.length > 0) {
      setDisableAddToBasket(true)
      errorOccurredRef.current = false
      setShowErrorMessage(false)
      await addToBasket(
        [
          ...selectedBundleProducts.map((product) => ({
            sku: product.sku.toString(),
            quantity: product.quantity,
          })),
        ],
        { fromRecommendations: false },
      )
      if (!errorOccurredRef.current) {
        router.push(`${Routes.Basket}`)
      }
    }
  }

  const isAddToBasketDisabled =
    !showAddToBasketErrorMessage && (basketLoading || disableAddToBasket)

  const bundleTitle =
    typeof document !== 'undefined' &&
    typeof window !== 'undefined' &&
    document.title

  return (
    <BuildYourOwnBundleContainer className={props.className}>
      <SummaryContainer>
        <StyledLeftMenu>
          <StyledTotalTitle
            textStyle={
              theme.widget.buildYourOwnBundle.components.progressIndicator.title
                .font
            }
          >
            {i18nText.yourTotalText}
          </StyledTotalTitle>
          {props.summaryDisplayType === SummaryDisplayType.DIAL ? (
            <ProgressIndicator
              currentIndicator={totalProductsInBundle}
              maxItems={props.maxProducts}
              type={ProgressIndicatorType.CIRCULAR}
              totalPrice={formattedTotalPrice}
              bundleSummaryAmount={props.summaryAmount}
              i18nText={i18nText.topProgressIndicatorBar}
              handleAddToBasketCallback={handleAddToBasketCallback}
              disabledAddToBasket={isAddToBasketDisabled}
              style={
                theme.widget.buildYourOwnBundle.components.progressIndicator
              }
            />
          ) : (
            <TotalReceiptBox>
              <TotalContent>
                <TotalContentText>
                  {`${totalProductsInBundle} ${i18nText.simpleItemsText}`}
                </TotalContentText>
                <TotalContentPrice>{formattedTotalPrice}</TotalContentPrice>
              </TotalContent>
              {(totalProductsInBundle === props.maxProducts ||
                ((props.unlimitedProducts || !props.maxProducts) &&
                  totalProductsInBundle > 0)) && (
                <AddTotalToBasket
                  onClick={handleAddToBasketCallback}
                  disabled={isAddToBasketDisabled}
                >
                  {i18nText.addToBasket.toUpperCase()}
                </AddTotalToBasket>
              )}
            </TotalReceiptBox>
          )}

          <React.Fragment>
            <StyledMenuTitle
              textStyle={
                theme.widget.buildYourOwnBundle.components.categories.title.font
              }
            >
              {i18nText.categoriesText}
            </StyledMenuTitle>
            <ul style={{ width: '100%' }}>{bundleCategories}</ul>
          </React.Fragment>
        </StyledLeftMenu>
        <div style={{ width: '100%' }}>
          {showAddToBasketErrorMessage && (
            <div ref={focusAddToBasketErrorMessage}>
              <AddToBasketErrorMessage
                error={i18nText.addToBasketError}
                id={`build-your-own-bundle-add-to-basket-error`}
              />
            </div>
          )}
          <TitleContainer>
            {props.showBundlePageTitle && (
              <Title
                textStyle={theme.widget.buildYourOwnBundle.components.h1.font}
                renderedAs="h1"
                content={bundleTitle || i18nText.bundleTitleText}
              />
            )}
            {props.bundleSubTitle && (
              <Subtitle
                textStyle={
                  theme.widget.buildYourOwnBundle.components.subtitle.font
                }
                renderedAs="h2"
                content={props.bundleSubTitle}
              />
            )}
          </TitleContainer>
          <ResponsiveBuildYourOwnBundleProducts
            {...props}
            focusedAccordionItem={focusedAccordionItem}
            setSelectedBundleProducts={setSelectedBundleProducts}
            selectedBundleProducts={selectedBundleProducts}
            availableBundleQuantitySelection={
              (props.maxProducts || MAX_QUANTITY_SELECTION) -
              totalProductsInBundle
            }
          />
        </div>
      </SummaryContainer>
      <ProgressIndicator
        type={ProgressIndicatorType.BAR}
        maxItems={props.maxProducts}
        currentIndicator={totalProductsInBundle}
        unlimitedProducts={props.unlimitedProducts}
        totalPrice={props.summaryAmount || formattedTotalPrice}
        i18nText={i18nText.bottomProgressIndicatorBar}
        handleAddToBasketCallback={handleAddToBasketCallback}
        disabledAddToBasket={isAddToBasketDisabled}
        style={theme.widget.buildYourOwnBundle.components.progressIndicator}
      />
    </BuildYourOwnBundleContainer>
  )
}
